import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, sizeLabel, usd } from '../lib/api';
import { ErrorNote, Flag, Modal, useLoad } from '../components/ui';
import NewItem from '../components/NewItem';

interface EditLine {
  itemCode: string | null; description: string; sizeMl: number | null; qtyCases: number; qtyBottles: number; casePack: number | null;
  unitCaseCents: number | null; extendedCents: number; productId: string | null; nonStock: boolean; label: string; flags: string[];
}

const toEdit = (l: any): EditLine => ({
  itemCode: l.wholesaler_item_code, description: l.description, sizeMl: l.size_ml, qtyCases: l.qty_cases, qtyBottles: l.qty_bottles,
  casePack: l.case_pack, unitCaseCents: l.unit_case_cents, extendedCents: l.extended_cents, productId: l.product_id,
  nonStock: l.flags.includes('NON_STOCK'), label: l.product_id ? `${l.brand} ${l.product_name} ${sizeLabel(l.product_size_ml)}` : '', flags: l.flags,
});

const dollars = (c: number | null) => (c == null ? '' : (c / 100).toFixed(2));
const cents = (s: string) => (s.trim() === '' ? null : Math.round(Number(s) * 100));

export default function InvoiceReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const inv = useLoad(() => api<any>(`/api/invoices/${id}`), [id]);
  const wholesalers = useLoad(() => api<any[]>('/api/wholesalers'), []);
  const [head, setHead] = useState<any>(null);
  const [lines, setLines] = useState<EditLine[]>([]);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [posted, setPosted] = useState<any>(null);
  const [img, setImg] = useState<{ url: string; pdf: boolean } | null>(null);
  const [picking, setPicking] = useState<number | null>(null);
  const [creating, setCreating] = useState<number | null>(null);
  const [addingWholesaler, setAddingWholesaler] = useState(false);

  useEffect(() => {
    if (!inv.data) return;
    setHead({ wholesalerId: inv.data.wholesaler_id ?? '', invoiceNo: inv.data.invoice_no ?? '', deliveryDate: inv.data.delivery_date ?? '', totalCents: inv.data.total_cents });
    setLines(inv.data.lines.map(toEdit));
    setDirty(false);
  }, [inv.data]);

  useEffect(() => {
    if (!inv.data?.image_uris?.length) return;
    let url = '';
    api<Response>(`/api/invoices/${id}/images/0`, { raw: true }).then((r) => r.blob()).then((b) => { url = URL.createObjectURL(b); setImg({ url, pdf: b.type === 'application/pdf' }); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [inv.data?.image_uris?.length, id]);

  const editable = inv.data?.status === 'needs_review';
  const set = (i: number, patch: Partial<EditLine>) => { setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l))); setDirty(true); };

  async function save() {
    setErr(null);
    try {
      await api(`/api/invoices/${id}`, { method: 'PATCH', body: {
        wholesalerId: head.wholesalerId || null, invoiceNo: head.invoiceNo || null, deliveryDate: head.deliveryDate || null, totalCents: head.totalCents,
        lines: lines.map(({ label, flags, ...l }) => l),
      } });
      inv.reload();
    } catch (e) { setErr(e); }
  }
  async function post() {
    setErr(null);
    try { if (dirty) await save(); const r = await api<any>(`/api/invoices/${id}/post`, { method: 'POST' }); setPosted(r); inv.reload(); }
    catch (e) { setErr(e); }
  }

  if (!inv.data || !head) return <div className="stack"><ErrorNote error={inv.error} />{!inv.error && <div className="empty">Loading…</div>}</div>;
  const d = inv.data;
  const lineTotal = lines.reduce((s, l) => s + (l.extendedCents || 0), 0);

  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>{d.wholesaler_name ?? 'New invoice'} {d.invoice_no ? `#${d.invoice_no}` : ''}</h1>
          <p>{editable ? 'Check the highlighted lines, fix anything misread, then post. Posting adds the stock, sets each item’s cost, creates the bill due 30 days after delivery, and records any RIP earned.' : 'Posted. Stock, costs and the bill are updated.'}</p></div>
        <Link to="/invoices">All invoices</Link>
      </div>
      <ErrorNote error={err} />
      {d.extraction?.notes?.map((n: string) => <div key={n} className="notice warn">{n}</div>)}
      {d.warnings.map((w: string) => <div key={w}><Flag code={w} /></div>)}
      {posted && (
        <div className="notice good">Posted. Bill of {usd(posted.bill.amount_cents)} is due {posted.bill.due_date}.{posted.rips.length ? ` RIP earned: ${posted.rips.map((r: any) => `${r.rip_code} ${usd(r.expected_cents)}`).join(', ')} — payable 30–90 days after you pay the invoice.` : ''}</div>
      )}
      <div className="review">
        <div className="shot">
          {img ? (img.pdf ? <iframe src={img.url} title="Invoice PDF" style={{ height: 600 }} /> : <img src={img.url} alt="Invoice photo" />) : <div className="panel empty">{d.image_uris.length ? 'Loading image…' : 'No image'}</div>}
        </div>
        <div className="stack">
          <div className="panel"><div className="panel-body field-row">
            <label>Vendor<select disabled={!editable} value={head.wholesalerId} onChange={(e) => { if (e.target.value === '__new') { setAddingWholesaler(true); return; } setHead({ ...head, wholesalerId: e.target.value }); setDirty(true); }}>
              <option value="">Choose…</option>{wholesalers.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              {editable && <option value="__new">+ Add a new vendor…</option>}
            </select></label>
            <label>Invoice number<input disabled={!editable} value={head.invoiceNo} onChange={(e) => { setHead({ ...head, invoiceNo: e.target.value }); setDirty(true); }} /></label>
            <label>Delivered on<input disabled={!editable} type="date" value={head.deliveryDate} onChange={(e) => { setHead({ ...head, deliveryDate: e.target.value }); setDirty(true); }} /></label>
            <label>Invoice total<input disabled={!editable} className="num-input" value={dollars(head.totalCents)} onChange={(e) => { setHead({ ...head, totalCents: cents(e.target.value) }); setDirty(true); }} /></label>
          </div></div>
          <div className="panel">
            <div className="table-wrap"><table className="grid">
              <thead><tr><th>Line as printed</th><th>Your item</th><th className="n">Cases</th><th className="n">Btl</th><th className="n">Case $</th><th className="n">Total $</th><th>Checks</th>{editable && <th />}</tr></thead>
              <tbody>{lines.map((l, i) => (
                <tr key={i}>
                  <td><div>{l.description}</div><div className="faint">{l.itemCode ?? ''} {l.sizeMl ? sizeLabel(l.sizeMl) : ''}</div></td>
                  <td>{l.nonStock ? <span className="faint">Charge (not stock)</span> : l.productId ? l.label || 'Matched' : <span className="chip bad">Not matched</span>}
                    {editable && !l.nonStock && <div><button className="ghost" style={{ minHeight: 0, padding: '2px 0' }} onClick={() => setPicking(i)}>{l.productId ? 'Change' : 'Pick item'}</button></div>}</td>
                  <td className="n">{editable ? <input className="n" inputMode="numeric" value={l.qtyCases} onChange={(e) => set(i, { qtyCases: Number(e.target.value) || 0 })} /> : l.qtyCases}</td>
                  <td className="n">{editable ? <input className="n" inputMode="numeric" value={l.qtyBottles} onChange={(e) => set(i, { qtyBottles: Number(e.target.value) || 0 })} /> : l.qtyBottles}</td>
                  <td className="n">{editable ? <input className="n" inputMode="decimal" value={dollars(l.unitCaseCents)} onChange={(e) => set(i, { unitCaseCents: cents(e.target.value) })} /> : usd(l.unitCaseCents)}</td>
                  <td className="n">{editable ? <input className="n" inputMode="decimal" value={dollars(l.extendedCents)} onChange={(e) => set(i, { extendedCents: cents(e.target.value) ?? 0 })} /> : usd(l.extendedCents)}</td>
                  <td>{l.flags.map((f) => <Flag key={f} code={f} />)}</td>
                  {editable && <td><button className="ghost" title="Mark as a charge, not stock" onClick={() => set(i, { nonStock: !l.nonStock, productId: l.nonStock ? l.productId : null })}>{l.nonStock ? 'Stock' : 'Charge'}</button></td>}
                </tr>
              ))}</tbody>
              <tfoot><tr><td colSpan={5} className="right" style={{ padding: '10px' }}>Lines add up to</td><td className="n" style={{ padding: '10px' }}>{usd(lineTotal)}</td><td colSpan={editable ? 2 : 1} /></tr></tfoot>
            </table></div>
          </div>
          {editable && (
            <div className="row">
              <button onClick={() => { setLines([...lines, { itemCode: null, description: 'New line', sizeMl: null, qtyCases: 1, qtyBottles: 0, casePack: null, unitCaseCents: 0, extendedCents: 0, productId: null, nonStock: false, label: '', flags: [] }]); setDirty(true); }}>Add a line</button>
              <span className="grow" />
              <button className="ghost" onClick={async () => { if (!confirm('Discard this invoice? Nothing has been added to your stock or bills.')) return; try { await api(`/api/invoices/${id}/discard`, { body: {} }); nav('/invoices'); } catch (e) { setErr(e); } }}>Discard</button>
              <button disabled={!dirty} onClick={save}>Save and re-check</button>
              <button className="primary big" onClick={post}>Post invoice</button>
            </div>
          )}
        </div>
      </div>
      {picking !== null && <PickItem onClose={() => setPicking(null)} onCreate={() => { setCreating(picking); setPicking(null); }} onPick={(it) => { set(picking, { productId: it.product_id, label: `${it.brand} ${it.name} ${sizeLabel(it.size_ml)}`, casePack: lines[picking]!.casePack ?? it.case_pack }); setPicking(null); }} />}
      {creating !== null && (
        <NewItem onClose={() => setCreating(null)}
          start={{ name: lines[creating]?.description, sizeMl: lines[creating]?.sizeMl, casePack: lines[creating]?.casePack, wholesalerId: head.wholesalerId || null, caseCents: lines[creating]?.unitCaseCents }}
          onDone={(r) => { set(creating, { productId: r.productId, label: `${r.brand} ${r.name} ${r.size}`, nonStock: false }); setCreating(null); }} />
      )}
      {addingWholesaler && <AddWholesaler onClose={() => setAddingWholesaler(false)} onDone={(w) => { wholesalers.reload(); setHead({ ...head, wholesalerId: w.id }); setDirty(true); setAddingWholesaler(false); }} />}
    </div>
  );
}

function AddWholesaler({ onClose, onDone }: { onClose: () => void; onDone: (w: any) => void }) {
  const [f, setF] = useState({ name: '', license: '', email: '' });
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Add a vendor" onClose={onClose} footer={<button className="primary" disabled={busy || f.name.trim().length < 2} onClick={async () => {
      setBusy(true); setErr(null);
      try { onDone(await api<any>('/api/wholesalers', { body: { name: f.name.trim(), njLicenseNo: f.license.trim() || null, email: f.email.trim() || null } })); } catch (e) { setErr(e); setBusy(false); }
    }}>Add vendor</button>}>
      <ErrorNote error={err} />
      <label>Name *<input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Fedway Associates" /></label>
      <label>NJ license number (optional)<input value={f.license} onChange={(e) => setF({ ...f, license: e.target.value })} /></label>
      <label>Email (optional)<input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
      <p className="faint">If this name is already on the list, you get the existing one back instead of a duplicate.</p>
    </Modal>
  );
}

function PickItem({ onClose, onPick, onCreate }: { onClose: () => void; onPick: (it: any) => void; onCreate: () => void }) {
  const [q, setQ] = useState('');
  const res = useLoad(() => (q.length >= 2 ? api<any[]>(`/api/items?q=${encodeURIComponent(q)}&limit=20`) : Promise.resolve([])), [q]);
  return (
    <Modal title="Match to an item" onClose={onClose}>
      <input autoFocus placeholder="Search your items" value={q} onChange={(e) => setQ(e.target.value)} />
      <div>{res.data?.map((it) => (
        <button key={it.id} className="ghost" style={{ width: '100%', justifyContent: 'space-between' }} onClick={() => onPick(it)}>
          <span>{it.brand} {it.name} · {sizeLabel(it.size_ml)}</span><span className="faint">{it.case_pack}/case</span>
        </button>))}
        {q.length >= 2 && res.data?.length === 0 && <p className="faint">No items match your search.</p>}
      </div>
      <div><button className="primary" onClick={onCreate}>Create a new item</button></div>
    </Modal>
  );
}
